const chapter = {
  id: 'nuclei',
  slug: 'nuclei',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Nuclei',
        hindi: 'नाभिक',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'composition',
      title: {
        english: 'Composition, Size & Density',
        hindi: 'संघटन, आकार और घनत्व',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Nucleus contains $Z$ protons and $N = A - Z$ neutrons (collectively, $A$ nucleons).',
        hindi: 'नाभिक में $Z$ प्रोटॉन और $N = A - Z$ न्यूट्रॉन (सामूहिक रूप से, $A$ न्यूक्लियॉन) होते हैं।',
            },
            {
              english: '**Notation**: ${}_Z^A X$. **Isotopes** (same $Z$, different $A$), **isobars** (same $A$, different $Z$), **isotones** (same $N$).',
        hindi: '**नोटेशन**: ${}_Z^A X$. **आइसोटोप** (समान $Z$, भिन्न $A$), **आइसोबार्स** (समान $A$, भिन्न $Z$), **आइसोटोन** (समान $N$)।',
            },
            {
              english: 'Mass: $1$ u $= 1.66\\times 10^{-27}$ kg $= 931.5$ MeV/$c^2$.',
        hindi: 'द्रव्यमान: $1$ u $= 1.66\\times 10^{-27}$ किग्रा $= 931.5$ MeV/$c^2$।',
            },
            {
              english: '**Nuclear radius**: $R = R_0 A^{1/3}$, $R_0 = 1.2$ fm.',
        hindi: '**परमाणु त्रिज्या**: $R = R_0 A^{1/3}$, $R_0 = 1.2$ एफएम।',
            },
            {
              english: '**Nuclear density** $\\approx 2.3\\times 10^{17}$ kg/m³ — independent of $A$.',
        hindi: '**परमाणु घनत्व** $\\approx 2.3\\times 10^{17}$ kg/m³ — $A$ से स्वतंत्र।',
            },
          ],
        },
      ],
    },
    {
      id: 'binding-energy',
      title: {
        english: 'Mass Defect & Binding Energy',
        hindi: 'द्रव्यमान न्यूनता और बंधन ऊर्जा',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Mass defect & BE',
        hindi: 'बड़े पैमाने पर दोष और बी.ई',
          },
          statement: {
            english: `$$\\Delta m = [Z m_p + (A - Z) m_n] - M_{\\text{nucleus}},$$
$$\\text{BE} = \\Delta m\\, c^2.$$
**Binding energy per nucleon** $= \\text{BE}/A$ — measure of stability.`,
            hindi: '$$\\Delta m = [Z m_p + (A - Z) m_n] - M_{\\text{nucleus}},$$ $$\\text{BE} = \\Delta m\\, c^2.$$ **प्रति न्यूक्लियॉन बंधन ऊर्जा** $= \\text{BE}/A$ - स्थिरता का माप।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'BE/A curve peaks near $A \\approx 56$ (Fe, Ni) at about **8.8 MeV/nucleon**.',
        hindi: 'BE/A वक्र $A \\approx 56$ (Fe, Ni) के करीब **8.8 MeV/न्यूक्लियॉन** पर चरम पर होता है।',
            },
            {
              english: 'Light nuclei → fusion releases energy (towards peak).',
        hindi: 'प्रकाश नाभिक → संलयन ऊर्जा जारी करता है (चरम की ओर)।',
            },
            {
              english: 'Heavy nuclei → fission releases energy (also towards peak).',
        hindi: 'भारी नाभिक → विखंडन से ऊर्जा निकलती है (चरम की ओर भी)।',
            },
            {
              english: 'BE/A drops for very small ($A<20$) and very large ($A>200$) nuclei.',
        hindi: 'बहुत छोटे ($A<20$) और बहुत बड़े ($A>200$) नाभिक के लिए BE/A बूंदें।',
            },
            {
              english: 'Energy released $Q = \\Delta m_{\\text{loss}}\\,c^2 = (\\sum \\text{BE}_{\\text{products}} - \\sum \\text{BE}_{\\text{reactants}})$.',
        hindi: 'ऊर्जा जारी $Q = \\Delta m_{\\text{loss}}\\,c^2 = (\\sum \\text{BE}_{\\text{products}} - \\sum \\text{BE}_{\\text{reactants}})$.',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: '**Nuclear force**: short-range ($\\sim 1$ fm), charge-independent, strongly attractive but with very short-range repulsion. Saturates — each nucleon binds only its neighbours.',
        hindi: '**परमाणु बल**: कम दूरी ($\\sim 1$ fm), चार्ज-स्वतंत्र, अत्यधिक आकर्षक लेकिन बहुत कम दूरी के प्रतिकर्षण के साथ। संतृप्त - प्रत्येक न्यूक्लियॉन केवल अपने पड़ोसियों को बांधता है।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Mass defect of $^4$He: $4.0026$ u versus $2(1.0073) + 2(1.0087) = 4.0320$ u. BE per nucleon?',
        hindi: '$^4$He का व्यापक दोष: $4.0026$ u बनाम $2(1.0073) + 2(1.0087) = 4.0320$ u। प्रति न्यूक्लियॉन बीई?',
          },
          solution: {
            english: '$\\Delta m = 0.0294$ u. BE $= 0.0294 \\cdot 931.5 = 27.4$ MeV. BE/A $= 27.4/4 = 6.85$ MeV.',
        hindi: '$\\Delta m = 0.0294$ यू. बीई $= 0.0294 \\cdot 931.5 = 27.4$ मेव। बीई/ए $= 27.4/4 = 6.85$ मेव।',
          },
        },
      ],
    },
    {
      id: 'radioactivity',
      title: {
        english: 'Radioactive Decay',
        hindi: 'रेडियोधर्मी क्षय',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**α-decay**: ${}_Z^A X \\to {}_{Z-2}^{A-4} Y + {}_2^4 \\text{He}$. $A$ decreases by 4, $Z$ by 2.',
        hindi: '**α-क्षय**: ${}_Z^A $A$ 4 से घटता है, $Z$ 2 से घटता है।',
            },
            {
              english: '**β⁻ decay**: $n \\to p + e^- + \\bar\\nu_e$. $Z$ increases by 1, $A$ unchanged.',
        hindi: '**β⁻ क्षय**: $n \\to p + e^- + \\bar\\nu_e$। $Z$ 1 से बढ़ता है, $A$ अपरिवर्तित रहता है।',
            },
            {
              english: '**β⁺ decay**: $p \\to n + e^+ + \\nu_e$. $Z$ decreases by 1.',
        hindi: '**β⁺ क्षय**: $p \\to n + e^+ + \\nu_e$. $Z$ 1 से घटता है।',
            },
            {
              english: '**γ-decay**: nucleus de-excites; $Z, A$ unchanged. Penetrating power: γ > β > α; ionising power: α > β > γ.',
        hindi: '**γ-क्षय**: केन्द्रक डी-उत्तेजित करता है; $Z, A$ अपरिवर्तित. भेदन शक्ति: γ > β > α; आयनीकरण शक्ति: α > β > γ.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Decay law',
        hindi: 'क्षय कानून',
          },
          statement: {
            english: `$$\\dfrac{dN}{dt} = -\\lambda N \\Rightarrow N(t) = N_0 e^{-\\lambda t}.$$
Activity $A(t) = \\lambda N(t) = A_0 e^{-\\lambda t}$.
Half-life $T_{1/2} = \\ln 2/\\lambda$. Mean life $\\tau = 1/\\lambda = T_{1/2}/\\ln 2$.`,
            hindi: '$$\\dfrac{dN}{dt} = -\\lambda N \\Rightarrow N(t) = N_0 e^{-\\lambda t}.$$ गतिविधि $A(t) = \\lambda N(t) = A_0 e^{-\\lambda t}$. आधा जीवन $T_{1/2} = \\ln 2/\\lambda$. औसत जीवन $\\tau = 1/\\lambda = T_{1/2}/\\ln 2$. **विखंडन**: भारी नाभिक दो मध्यम नाभिक + न्यूट्रॉन + ऊर्जा में विभाजित होता है। उदाहरण: \\\\$$\\dfrac{dN}{dt} = -\\lambda N \\Rightarrow N(t) = N_0 e^{-\\lambda t}.$$ मेव। अनियंत्रित (परमाणु बम) या मॉडरेटर (रिएक्टर) से नियंत्रित होने पर 2-3 न्यूट्रॉन छोड़ता है → **श्रृंखला प्रतिक्रिया**।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'After $n$ half-lives: $N = N_0/2^n$.',
        hindi: '$n$ आधे जीवन के बाद: $N = N_0/2^n$।',
            },
            {
              english: 'Unit of activity: **becquerel** (1 Bq = 1 disint/s); $1$ curie $= 3.7\\times 10^{10}$ Bq.',
        hindi: 'गतिविधि की इकाई: **बेकरेल** (1 बीक्यू = 1 डिसिंट/एस); $1$ क्यूरी $= 3.7\\times 10^{10}$ Bq.',
            },
            {
              english: 'Number of α and β particles emitted in chain ${}_Z^A X \\to {}_{Z\'}^{A\'} Y$: $n_\\alpha = (A - A\')/4$, $n_\\beta = 2 n_\\alpha - (Z - Z\')$.',
        hindi: 'श्रृंखला ${}_Z^A X \\to {}_{Z\'}^{A\'} Y$ में उत्सर्जित α और β कणों की संख्या: $n_\\alpha = (A - A\')/4$, $n_\\beta = 2 n_\\alpha - (Z - Z\')$.',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A radioactive sample has half-life 20 min. What fraction remains after 1 hour?',
        hindi: 'एक रेडियोधर्मी नमूने का आधा जीवन 20 मिनट है। 1 घंटे के बाद कौन सा अंश बचता है?',
          },
          solution: {
            english: '$n = 60/20 = 3$ half-lives. Fraction $= 1/2^3 = 1/8$.',
        hindi: '$n = 60/20 = 3$ आधा जीवन। अंश $= 1/2^3 = 1/8$. **क्रिटिकल द्रव्यमान**: आत्मनिर्भर श्रृंखला प्रतिक्रिया के लिए न्यूनतम द्रव्यमान।',
          },
        },
      ],
    },
    {
      id: 'fission-fusion',
      title: {
        english: 'Nuclear Fission & Fusion',
        hindi: 'नाभिकीय विखण्डन और संलयन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: `**Fission**: heavy nucleus splits into two medium nuclei + neutrons + energy. Example:
\${}^{235}\\text{U} + n \\to {}^{141}\\text{Ba} + {}^{92}\\text{Kr} + 3 n + 200$ MeV. Releases 2–3 neutrons → **chain reaction** if uncontrolled (atomic bomb) or controlled with moderators (reactor).`,
            hindi: '**विखंडन**: भारी नाभिक दो मध्यम नाभिक + न्यूट्रॉन + ऊर्जा में विभाजित हो जाता है। उदाहरण: \\\\${}^{235}\\text{U} + n \\to {}^{141}\\text{Ba} + {}^{92}\\text{Kr} + 3 n + 200$ मेव। अनियंत्रित (परमाणु बम) या मॉडरेटर (रिएक्टर) से नियंत्रित होने पर 2-3 न्यूट्रॉन छोड़ता है → **श्रृंखला प्रतिक्रिया**।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Critical mass**: minimum mass for self-sustaining chain reaction.',
        hindi: '**क्रांतिक द्रव्यमान**: आत्मनिर्भर श्रृंखला प्रतिक्रिया के लिए न्यूनतम द्रव्यमान।',
            },
            {
              english: 'Reactor components: fuel ($^{235}$U / $^{239}$Pu), moderator (heavy water, graphite — slows neutrons), control rods (Cd, B — absorb neutrons), coolant.',
        hindi: 'रिएक्टर घटक: ईंधन ($^{235}$U / $^{239}$Pu), मॉडरेटर (भारी पानी, ग्रेफाइट - न्यूट्रॉन को धीमा करता है), नियंत्रण छड़ें (Cd, B - न्यूट्रॉन को अवशोषित करता है), शीतलक।',
            },
            {
              english: '**Fusion**: light nuclei combine to form heavier one + energy. Requires very high $T$ (~$10^7$ K) to overcome Coulomb barrier. Source of energy in stars.',
        hindi: '**संलयन**: हल्के नाभिक मिलकर भारी नाभिक + ऊर्जा बनाते हैं। कूलम्ब बाधा पर काबू पाने के लिए बहुत उच्च $T$ (~$10^7$ K) की आवश्यकता होती है। तारों में ऊर्जा का स्रोत.',
            },
            {
              english: 'Sun\'s energy: **proton–proton cycle** $4\\,{}^1\\text{H} \\to {}^4\\text{He} + 2 e^+ + 2\\nu + 26.7$ MeV.',
        hindi: 'सूर्य की ऊर्जा: **प्रोटॉन-प्रोटॉन चक्र** $4\\,{}^1\\text{H} \\to {}^4\\text{He} + 2 e^+ + 2\\nu + 26.7$ MeV।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Fission yields ~$200$ MeV per nucleus, fusion yields ~$26.7$ MeV per cycle but **per nucleon** fusion releases more energy (~$6$ MeV/nucleon vs ~$0.85$ MeV/nucleon for fission).',
        hindi: 'विखंडन से ~$200$ MeV प्रति नाभिक प्राप्त होता है, संलयन से ~$26.7$ MeV प्रति चक्र प्राप्त होता है, लेकिन **प्रति न्यूक्लियॉन** संलयन से अधिक ऊर्जा निकलती है (विखंडन के लिए ~$6$ MeV/nucleon बनाम ~$0.85$ MeV/nucleon)।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'R = R_0 A^{1/3}',
      desc: {
        english: 'Nuclear radius',
        hindi: 'परमाणु त्रिज्या',
      },
    },
    {
      latex: '\\text{BE} = \\Delta m\\, c^2',
      desc: {
        english: 'Binding energy',
        hindi: 'बंधनकारी ऊर्जा',
      },
    },
    {
      latex: 'N(t) = N_0 e^{-\\lambda t}',
      desc: {
        english: 'Decay law',
        hindi: 'क्षय कानून',
      },
    },
    {
      latex: 'T_{1/2} = \\ln 2/\\lambda',
      desc: {
        english: 'Half-life',
        hindi: 'आधा जीवन',
      },
    },
    {
      latex: '\\tau = 1/\\lambda',
      desc: {
        english: 'Mean life',
        hindi: 'मतलबी जीवन',
      },
    },
    {
      latex: 'A = \\lambda N',
      desc: {
        english: 'Activity',
        hindi: 'गतिविधि',
      },
    },
    {
      latex: '1\\ \\text{u} = 931.5\\ \\text{MeV}/c^2',
      desc: {
        english: 'Mass-energy',
        hindi: 'द्रव्यमान-ऊर्जा',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Decay',
      problem: {
        english: 'After 3 half-lives, what fraction of nuclei has decayed?',
        hindi: '3 आधे जीवन के बाद, नाभिक का कितना भाग क्षय हो गया है?',
      },
      answer: {
        english: '$7/8$',
        hindi: '$7/8$',
      },
      solution: {
        english: 'Remaining $= 1/8$, decayed $= 1 - 1/8 = 7/8$.',
        hindi: 'शेष $= 1/8$, क्षयग्रस्त $= 1 - 1/8 = 7/8$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Radius',
      problem: {
        english: 'Ratio of radii of $^{27}$Al and $^{125}$Te?',
        hindi: '$^{27}$Al और $^{125}$Te की त्रिज्याओं का अनुपात?',
      },
      answer: {
        english: '$3 : 5$',
        hindi: '$3 : 5$',
      },
      solution: {
        english: '$R \\propto A^{1/3}$. $27^{1/3}/125^{1/3} = 3/5$.',
        hindi: '$R \\propto A^{1/3}$. $27^{1/3}/125^{1/3} = 3/5$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Mean life',
      problem: {
        english: 'Half-life is 20 s. Mean life?',
        hindi: 'आधा जीवन 20 सेकंड है। मतलब जीवन?',
      },
      answer: {
        english: '$28.85$ s',
        hindi: '$28.85$ s',
      },
      solution: {
        english: '$\\tau = T_{1/2}/\\ln 2 = 20/0.693$.',
        hindi: '$\\tau = T_{1/2}/\\ln 2 = 20/0.693$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'BE/A',
      problem: {
        english: 'BE/nucleon is maximum near which mass number?',
        hindi: 'BE/nucleon किस द्रव्यमान संख्या के निकट अधिकतम है?',
      },
      answer: {
        english: '$A \\approx 56$',
        hindi: '$A \\approx 56$',
      },
      solution: {
        english: 'Iron-region peak.',
        hindi: 'लौह-क्षेत्र शिखर।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'α/β count',
      problem: {
        english: 'In decay chain ${}^{238}_{92}\\text{U} \\to {}^{206}_{82}\\text{Pb}$, number of α and β particles?',
        hindi: 'क्षय श्रृंखला ${}^{238}_{92}\\text{U} \\to {}^{206}_{82}\\text{Pb}$ में, α और β कणों की संख्या?',
      },
      answer: {
        english: '$8$ α and $6$ β',
        hindi: '$8$ α और $6$ β',
      },
      solution: {
        english: '$n_\\alpha = (238-206)/4 = 8$. $n_\\beta = 2\\cdot 8 - (92-82) = 6$.',
        hindi: '$n_\\alpha = (238-206)/4 = 8$. $n_\\beta = 2\\cdot 8 - (92-82) = 6$.',
      },
    },
  ],
  next: {
    slug: 'semiconductors-diodes',
    title: 'Semiconductors & Diodes',
  },
};

export default chapter;
